import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CheckboxFilterDescriptor,
  type CustomFilterDescriptor,
  FilterKind,
  MULTI_VALUE_FILTER_DELIMITER,
  type RadioFilterDescriptor,
  type TableFilterDescriptor,
  type TableFilterOption,
} from '@fly/shared';
import type { TFunction } from 'i18next';

import { FilterCheckboxGroup } from '../FilterCheckboxGroup';
import { FilterRadioGroup } from '../FilterRadioGroup';

function parseMultiValue(value: string | undefined): string[] {
  if (!value) return [];
  return value.split(MULTI_VALUE_FILTER_DELIMITER).filter(Boolean);
}

/**
 * Converts declarative filter descriptors to TableFilterOption[] for use with
 * TanstackTable and FlyTableControls. Skips search descriptors (search is handled by the table).
 */
export function useFilterDescriptors<T extends object>(
  descriptors: TableFilterDescriptor<T>[]
): TableFilterOption<T>[] {
  const { t } = useTranslation();

  const [dynamicOptions, setDynamicOptions] = useState<
    Record<
      string,
      {
        value: string;
        label: [string, { defaultValue?: string }];
        icon?: string;
      }[]
    >
  >({});
  const [dynamicLoading, setDynamicLoading] = useState<Record<string, boolean>>(
    {}
  );
  const loadedKeysRef = useRef<Set<string>>(new Set());

  const nonSearch = useMemo(
    () => descriptors.filter((d) => d.kind !== FilterKind.SEARCH),
    [descriptors]
  );

  useEffect(() => {
    const toLoad = nonSearch.filter(
      (d): d is RadioFilterDescriptor<T> | CheckboxFilterDescriptor<T> =>
        (d.kind === FilterKind.RADIO || d.kind === FilterKind.CHECKBOX) &&
        d.optionsSource.type === 'dynamic'
    );
    toLoad.forEach((d) => {
      const key = d.key;
      const optionsSource = d.optionsSource;
      if (optionsSource.type !== 'dynamic' || loadedKeysRef.current.has(key))
        return;
      loadedKeysRef.current.add(key);
      setDynamicLoading((prev) => ({ ...prev, [key]: true }));
      Promise.resolve(optionsSource.getOptions())
        .then((opts) => {
          setDynamicOptions((prev) => ({
            ...prev,
            [key]: opts.map((o) => ({
              value: o.value,
              label: o.label as [string, { defaultValue?: string }],
              icon: o.icon,
            })),
          }));
        })
        .finally(() => {
          setDynamicLoading((prev) => ({ ...prev, [key]: false }));
        });
    });
  }, [nonSearch]);

  const toFilterOption = useCallback(
    (d: TableFilterDescriptor<T>): TableFilterOption<T> | null => {
      if (d.kind === FilterKind.SEARCH) return null;

      const name = d.label;
      const getSelectedName = (
        value: string | undefined
      ): Parameters<TFunction> => {
        if (!value) return name;
        const values = parseMultiValue(value);
        if (values.length <= 1) return [value, { defaultValue: value }];
        return [
          `${values.length} selected`,
          { defaultValue: `${values.length} selected` },
        ];
      };

      if (d.kind === FilterKind.RADIO) {
        const opts =
          d.optionsSource.type === 'static'
            ? d.optionsSource.options
            : (dynamicOptions[d.key] ?? []).map((o) => ({
                value: o.value,
                label: o.label,
                icon: o.icon,
              }));
        const loading =
          d.optionsSource.type === 'dynamic' && dynamicLoading[d.key];
        const clientFilterFn =
          d.optionsSource.type === 'static'
            ? d.optionsSource.clientFilterFn
            : d.optionsSource.filterFn;
        return {
          key: d.key,
          name,
          getSelectedName: (
            value: string | undefined
          ): Parameters<TFunction> => {
            if (!value) return name;
            const opt = opts.find((o) => o.value === value);
            return opt ? opt.label : [value, { defaultValue: value }];
          },
          possibleValues: opts.map((o) => ({
            name: o.label,
            value: o.value,
            filter: clientFilterFn
              ? (row: T) => clientFilterFn(row, o.value)
              : () => false,
          })),
          hideFromAddDropdown: d.hideFromAddDropdown,
          applyClientSide: !!clientFilterFn,
          FilterView: ({ filter, onChange, onRemove }) => (
            <FilterRadioGroup
              name={d.key}
              options={opts}
              value={filter.value}
              onChange={onChange}
              onRemove={onRemove}
              loading={loading}
            />
          ),
        };
      }

      if (d.kind === FilterKind.CHECKBOX) {
        const opts =
          d.optionsSource.type === 'static'
            ? d.optionsSource.options
            : (dynamicOptions[d.key] ?? []).map((o) => ({
                value: o.value,
                label: o.label,
                icon: o.icon,
              }));
        const loading =
          d.optionsSource.type === 'dynamic' && dynamicLoading[d.key];
        const clientFilterFn =
          d.optionsSource.type === 'static'
            ? d.optionsSource.clientFilterFn
            : d.optionsSource.filterFn;
        return {
          key: d.key,
          name,
          getSelectedName,
          possibleValues: opts.map((o) => ({
            name: o.label,
            value: o.value,
            filter: clientFilterFn
              ? (row: T) => clientFilterFn(row, o.value)
              : () => false,
          })),
          hideFromAddDropdown: d.hideFromAddDropdown,
          applyClientSide: !!clientFilterFn,
          FilterView: ({ filter, onChange, onRemove }) => (
            <FilterCheckboxGroup
              options={opts}
              value={filter.value}
              onChange={onChange}
              onRemove={onRemove}
              loading={loading}
            />
          ),
        };
      }

      if (d.kind === FilterKind.CUSTOM) {
        const custom = d as CustomFilterDescriptor<T>;
        return {
          key: d.key,
          name,
          getSelectedName: (
            value: string | undefined
          ): Parameters<TFunction> =>
            value ? [value, { defaultValue: value }] : name,
          possibleValues: [],
          hideFromAddDropdown: d.hideFromAddDropdown,
          FilterView: ({ filter, onChange, onRemove }) => (
            <custom.FilterView
              filter={filter}
              value={filter.value}
              onChange={onChange}
              onRemove={onRemove}
            />
          ),
        };
      }

      return null;
    },
    [t, dynamicOptions, dynamicLoading]
  );

  return useMemo(() => {
    const result: TableFilterOption<T>[] = [];
    for (const d of nonSearch) {
      const opt = toFilterOption(d);
      if (opt) result.push(opt);
    }
    return result;
  }, [nonSearch, toFilterOption]);
}
